import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * ParticipantCalendarTab — read-only participant-filtered calendar view.
 *
 * Aggregates events client-side from existing authoritative sources:
 *   - shifts     (via /api/roster/shifts?participantId)
 *   - appointments (via /api/appointments?participantId)
 *   - group_sessions (via /api/group-sessions/participant/:id/sessions)
 *
 * Does NOT copy or store any calendar record. Displays references to
 * authoritative source records only. Duplicate suppression: entries are
 * deduped by `${source}:${id}` so the same underlying record surfacing
 * through multiple sources is shown only once.
 */
export function ParticipantCalendarTab({ participantId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);

    const safeGet = (url) => api.get(url).then((r) => r?.data).catch((e) => {
      if (e?.response?.status === 403) return { __denied: true };
      return null;
    });

    Promise.all([
      safeGet(`/roster/shifts?participantId=${participantId}&pageSize=50`),
      safeGet(`/appointments?participantId=${participantId}&pageSize=50`),
      safeGet(`/group-sessions/participant/${participantId}/sessions`),
    ]).then(([shiftsResp, aptsResp, sessionsResp]) => {
      if (cancelled) return;
      if ([shiftsResp, aptsResp, sessionsResp].every((r) => r && r.__denied)) {
        setDenied(true); return;
      }
      const shiftItems = shiftsResp?.items || shiftsResp?.data || [];
      const aptItems = aptsResp?.items || aptsResp?.data || [];
      const sessionItems = sessionsResp?.items || sessionsResp?.data || sessionsResp || [];

      const merged = [];
      const seen = new Set();
      const push = (source, id, when, title, badge) => {
        const key = `${source}:${id}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push({ key, source, id, when, title, badge });
      };

      (Array.isArray(shiftItems) ? shiftItems : []).forEach((s) => {
        const id = s.id || s._id;
        const when = `${s.date || ''} ${s.start_time || ''}`.trim();
        push('shift', id, when, s.title || s.shift_type || 'Shift', s.status);
      });
      (Array.isArray(aptItems) ? aptItems : []).forEach((a) => {
        const id = a.id || a._id;
        const when = a.scheduledStart || '';
        push('appointment', id, when, a.title || a.appointmentType || 'Appointment', a.status);
      });
      (Array.isArray(sessionItems) ? sessionItems : []).forEach((g) => {
        const id = g.id || g._id;
        const when = g.session_date || g.date || '';
        push('activity', id, when, g.title || g.name || 'Activity', g.status);
      });

      merged.sort((x, y) => (x.when || '').localeCompare(y.when || ''));
      setEvents(merged);
    }).catch(() => {
      if (!cancelled) setError('Unable to load calendar for this participant.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [participantId]);

  const grouped = useMemo(() => {
    const g = { shift: 0, appointment: 0, activity: 0 };
    events.forEach((e) => { g[e.source] = (g[e.source] || 0) + 1; });
    return g;
  }, [events]);

  if (denied) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="participant-calendar-denied">
        You do not have permission to view calendar events for this participant.
      </div>
    );
  }
  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="participant-calendar-loading">Loading calendar…</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-red-600" data-testid="participant-calendar-error">{error}</div>;
  }
  if (events.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center" data-testid="participant-calendar-empty">
        No calendar events linked to this participant.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="participant-calendar-tab">
      <div className="flex items-center gap-2 text-xs" data-testid="participant-calendar-summary">
        <Badge variant="outline">Shifts: {grouped.shift || 0}</Badge>
        <Badge variant="outline">Appointments: {grouped.appointment || 0}</Badge>
        <Badge variant="outline">Activities: {grouped.activity || 0}</Badge>
      </div>
      <div className="space-y-2">
        {events.map((e) => (
          <Card key={e.key} data-testid={`participant-calendar-row-${e.source}-${e.id}`}>
            <CardContent className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {e.title}
                  {e.when ? <span className="text-xs text-muted-foreground ml-2">{e.when}</span> : null}
                </p>
                <p className="text-xs text-muted-foreground">Source: {e.source}</p>
              </div>
              {e.badge ? <Badge variant="outline" className="text-[10px]">{e.badge}</Badge> : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ParticipantCalendarTab;
